// Operations Safety, Cohort Risk, Wellbeing Actions and Methodology sections.

import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { DerivedMetricKey, FatigueAction, FatigueFilters } from '../data/types';
import { useFatigueSummary } from '../data/useFatigueData';
import { useIndividualAccess } from '../data/useIndividualAccess';
import { ACTIONS_ARE_EXAMPLES, EXAMPLE_ACTIONS } from '../data/actionFixtures';
import { METRIC_DEFINITIONS, NOT_MEDICAL, PILLARS, RAG_PROVISIONAL_NOTE, WHY_DERIVED } from '../data/methodology';
import { CohortRiskTable } from '../components/CohortRiskTable';
import { CohortDetailPanel } from '../components/CohortDetailPanel';
import { IndividualAccessNotice, IndividualsPanel } from '../components/IndividualsPanel';
import { WearCompliancePanel } from '../components/WearCompliancePanel';
import { ActionMatrixChart, CohortQuadrantChart, ProjectionChart } from '../components/charts';
import { LoadingBlock, NoteLine, RagRangeTable, SectionHeading, TierBadge } from '../components/primitives';

/** Shared empty state — the API returned a window with nothing measurable in it. */
const NoData: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <p className='text-sm text-muted-foreground'>
    {children ?? 'No usable wearable data in this window for the current filters.'}
  </p>
);

// ── Operations Safety ───────────────────────────────────────────────────────

export const OperationsSection: React.FC<{ filters: FatigueFilters }> = ({ filters }) => {
  const { data, isPending } = useFatigueSummary(filters);
  const cohorts = data?.visibleCohorts ?? [];
  const measured = cohorts.filter((row) => !row.suppressed && !row.noData);

  return (
    <div className='flex flex-col gap-6'>
      {/* FM-11 */}
      <Card>
        <CardHeader>
          <SectionHeading
            overline='Projection'
            title='If nothing changes'
            subtitle='Stepped through two weeks at a time so each block can be read properly.'
          />
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {isPending ? (
            <LoadingBlock className='h-72' />
          ) : data && !data.empty ? (
            <>
              <ProjectionChart projection={data.projection} view='blocks' />
              <NoteLine>{data.projection.assumption}</NoteLine>
            </>
          ) : (
            <NoData />
          )}
        </CardContent>
      </Card>

      {/* FM-12 */}
      <Card>
        <CardHeader>
          <SectionHeading
            overline='Projection'
            title='Full 12-week horizon'
            subtitle='The same projection with every forward week visible at once.'
          />
        </CardHeader>
        <CardContent>
          {isPending ? (
            <LoadingBlock className='h-72' />
          ) : data && !data.empty ? (
            <ProjectionChart projection={data.projection} view='full' height={300} />
          ) : (
            <NoData />
          )}
        </CardContent>
      </Card>

      {/*
        This card used to be a rota overlay with start times, rest windows and
        consecutive-shift counts. None of that exists in the data — capturing
        shift patterns is PEAK-169 / PEAK-188 — so it now shows the one piece of
        timing context the platform does hold, labelled as the inference it is.
      */}
      <Card>
        <CardHeader>
          <SectionHeading
            overline='Load & timing'
            title='Shift pattern by team'
            subtitle='Inferred from how people described their working hours at sign-up.'
          />
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {isPending ? (
            <LoadingBlock />
          ) : cohorts.length ? (
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Shift pattern</TableHead>
                    <TableHead className='text-right'>People</TableHead>
                    <TableHead className='text-right'>Wearing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cohorts.map((row) => (
                    <TableRow key={row.cohort.id}>
                      <TableCell className='font-medium'>{row.cohort.name}</TableCell>
                      <TableCell className='text-muted-foreground'>{row.cohort.site}</TableCell>
                      <TableCell className='capitalize'>{row.cohort.shiftType}</TableCell>
                      <TableCell className='text-right tabular-nums'>{row.cohort.headcount}</TableCell>
                      <TableCell className='text-right tabular-nums'>
                        {row.compliance.activeUsers}/{row.compliance.enrolledUsers}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <NoData>No teams match the current filters.</NoData>
          )}
          <NoteLine>
            Shift pattern is each team&apos;s most common working-hours answer, not a rota. Start times, rest windows,
            overtime and consecutive shifts are not captured yet, so nothing on this page reads them (PEAK-169,
            PEAK-188).
          </NoteLine>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <SectionHeading
            overline='Where the pressure is'
            title='Disruption against misalignment'
            subtitle='Bubble size is headcount. Teams to the top right need attention first.'
          />
        </CardHeader>
        <CardContent>
          {isPending ? (
            <LoadingBlock />
          ) : measured.length ? (
            <CohortQuadrantChart summaries={measured} />
          ) : (
            <NoData>No team has enough data to place on this chart yet.</NoData>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Cohort Risk ─────────────────────────────────────────────────────────────

export const CohortRiskSection: React.FC<{ filters: FatigueFilters }> = ({ filters }) => {
  const [cohortId, setCohortId] = React.useState<string | null>(null);
  const [individualsCohortId, setIndividualsCohortId] = React.useState<string | null>(null);
  const access = useIndividualAccess();

  const { data, isPending } = useFatigueSummary(filters);
  const summaries = data?.visibleCohorts ?? [];

  // FM-7 — drilling into people is only offered when FM-10 allows it.
  const drill = access.allowed
    ? (id: string) => {
        setCohortId(null);
        setIndividualsCohortId(id);
      }
    : undefined;

  const individualsCohortName = summaries.find((s) => s.cohort.id === individualsCohortId)?.cohort.name;

  return (
    <div className='flex flex-col gap-6'>
      <Card>
        <CardHeader>
          <SectionHeading
            overline='Cohort risk'
            title='Teams ranked by fatigue exposure'
            subtitle={
              access.allowed
                ? 'Click a team name for individual-level detail, or the info icon for the cohort breakdown.'
                : 'Click the info icon on a row for the cohort breakdown.'
            }
          />
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          {isPending ? (
            <LoadingBlock className='h-64' />
          ) : summaries.length > 0 ? (
            <CohortRiskTable summaries={summaries} onOpenCohort={setCohortId} onDrillToIndividuals={drill} />
          ) : (
            <NoData>No cohorts match the current filters.</NoData>
          )}

          {/* FM-10 */}
          {!access.allowed && <IndividualAccessNotice reason={access.reason} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <SectionHeading
            overline='Data quality'
            title='Wear compliance'
            subtitle="How much of each team's data is usable in this window."
          />
        </CardHeader>
        <CardContent>
          {isPending ? (
            <LoadingBlock />
          ) : summaries.length ? (
            <WearCompliancePanel rows={summaries.map((row) => row.compliance)} />
          ) : (
            <NoData>No cohorts match the current filters.</NoData>
          )}
        </CardContent>
      </Card>

      <CohortDetailPanel
        cohortId={cohortId}
        filters={filters}
        onClose={() => setCohortId(null)}
        onDrillToIndividuals={drill}
      />
      <IndividualsPanel
        cohortId={individualsCohortId}
        cohortName={individualsCohortName}
        filters={filters}
        allowed={access.allowed}
        onClose={() => setIndividualsCohortId(null)}
      />
    </div>
  );
};

// ── Wellbeing Actions ───────────────────────────────────────────────────────

const STATUS_LABEL: Record<FatigueAction['status'], string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

const ActionRow: React.FC<{ action: FatigueAction }> = ({ action }) => (
  <div className='flex flex-col gap-2 rounded-lg border border-border p-4'>
    <div className='flex flex-wrap items-start justify-between gap-2'>
      <h3 className='text-sm font-semibold'>{action.title}</h3>
      <Badge variant={action.status === 'done' ? 'success' : 'outline'}>{STATUS_LABEL[action.status]}</Badge>
    </div>
    <p className='text-sm text-muted-foreground'>{action.description}</p>
    <dl className='flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground'>
      <div className='flex gap-1'>
        <dt>Impact</dt>
        <dd className='font-medium text-foreground'>{action.impact}/5</dd>
      </div>
      <div className='flex gap-1'>
        <dt>Ease</dt>
        <dd className='font-medium text-foreground'>{action.ease}/5</dd>
      </div>
      <div className='flex gap-1'>
        <dt>Owner</dt>
        <dd className='font-medium text-foreground'>{action.owner}</dd>
      </div>
      <div className='flex gap-1'>
        <dt>Due</dt>
        <dd className='font-medium text-foreground'>{action.dueDate}</dd>
      </div>
    </dl>
  </div>
);

export const ActionsSection: React.FC = () => (
  <div className='flex flex-col gap-6'>
    <Card>
      <CardHeader>
        <SectionHeading
          overline='Wellbeing actions'
          title='Impact against ease'
          subtitle='Top-right first: high impact, straightforward to do.'
        />
      </CardHeader>
      <CardContent className='flex flex-col gap-3'>
        <ActionMatrixChart actions={EXAMPLE_ACTIONS} />
        <NoteLine>{ACTIONS_ARE_EXAMPLES}</NoteLine>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <SectionHeading title='Example actions' subtitle='Highest impact first.' />
      </CardHeader>
      <CardContent className='grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4'>
        {[...EXAMPLE_ACTIONS]
          .sort((a, b) => b.impact - a.impact)
          .map((action) => (
            <ActionRow key={action.id} action={action} />
          ))}
      </CardContent>
    </Card>
  </div>
);

// ── Methodology ─────────────────────────────────────────────────────────────

const METRIC_KEYS: DerivedMetricKey[] = ['fed', 'lrm', 'cdi', 'pdc'];

export const MethodologySection: React.FC = () => (
  <div className='flex flex-col gap-6'>
    <Card>
      <CardHeader>
        <SectionHeading
          overline='Methodology'
          title='The three pillars'
          subtitle='Every derived indicator is built from these.'
        />
      </CardHeader>
      <CardContent className='grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4'>
        {PILLARS.map((pillar) => (
          <div key={pillar.title} className='flex flex-col gap-1.5 rounded-lg border border-border p-4'>
            <h3 className='text-sm font-semibold'>{pillar.title}</h3>
            <p className='text-sm text-muted-foreground'>{pillar.body}</p>
          </div>
        ))}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <SectionHeading title='Why PEAK4 uses its own indicators' />
      </CardHeader>
      <CardContent>
        <p className='text-sm text-muted-foreground'>{WHY_DERIVED}</p>
      </CardContent>
    </Card>

    {/* FM-16 */}
    <Card>
      <CardHeader>
        <SectionHeading title='RAG ranges' subtitle='The bands behind every green, amber and red on this dashboard.' />
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        <div className='grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4'>
          {METRIC_KEYS.map((key) => (
            <div key={key} className='flex flex-col gap-2'>
              <h3 className='text-sm font-semibold'>
                {METRIC_DEFINITIONS[key].code}
                <span className='ml-1.5 font-normal text-muted-foreground'>{METRIC_DEFINITIONS[key].name}</span>
              </h3>
              <RagRangeTable metricKey={key} />
            </div>
          ))}
        </div>
        <NoteLine>{RAG_PROVISIONAL_NOTE}</NoteLine>
        <p className='text-sm text-muted-foreground'>
          A cohort takes the worst tier of its four indicators: <TierBadge tier='red' /> if any indicator is red,
          otherwise <TierBadge tier='amber' /> if any is amber.
        </p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <SectionHeading title='Scope and limits' />
      </CardHeader>
      <CardContent className='flex flex-col gap-3'>
        <p className='text-sm text-muted-foreground'>{NOT_MEDICAL}</p>
        <p className='text-sm text-muted-foreground'>
          Indicator values are computed from connected wearable data — sleep sessions, recovery and daily strain — over
          the window shown in the filter bar. Each person&apos;s deviation cluster is judged against their own preceding
          baseline, never against a population norm, and a fortnight with no usable data is left out rather than counted
          as zero.
        </p>
        <p className='text-sm text-muted-foreground'>
          Two limits are worth stating plainly. There is no rota data, so every day a person wore a device is treated as
          a workday and shift pattern is inferred from their sign-up answer (PEAK-169, PEAK-188). And the numeric
          thresholds behind each indicator, along with the RAG bands above, are still provisional pending sign-off
          (PEAK-185) — the definitions they implement are in the API alongside the reason for each cut-off.
        </p>
        <p className='text-sm text-muted-foreground'>
          The wellbeing actions section is the one place figures are illustrative rather than measured (PEAK-179).
        </p>
      </CardContent>
    </Card>
  </div>
);
